import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastProvider, useToast } from "../components/Toast";
import AdminSidebar from "../components/AdminSidebar";
import BlogForm, {
  EMPTY_BLOG_FORM,
  buildBlogFormData,
  validateBlogForm,
} from "../components/BlogForm";
import { fetchBlogAdmin, updateBlog } from "../../services/api";

const EditBlogInner = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [existingHeadImage, setExistingHeadImage] = useState(null);
  const [headImageFile, setHeadImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchBlogAdmin(id)
      .then((res) => {
        const b = res.data.data;
        setForm({
          ...EMPTY_BLOG_FORM,
          title: b.title,
          slug: b.slug,
          seoTitle: b.seoTitle,
          metaDescription: b.metaDescription,
          keywords: b.keywords || "",
          category: b.category || "General",
          headImageAlt: b.headImageAlt || "",
          content: b.content,
          customSchema: b.customSchema || "",
          status: b.status,
        });
        setExistingHeadImage(b.headImage);
        try {
          const parsed = JSON.parse(b.faqs || "[]");
          setFaqs(Array.isArray(parsed) ? parsed : []);
        } catch {
          setFaqs([]);
        }
      })
      .catch(() => setNotFound(true));
  }, [id]);

  const handleSubmit = async () => {
    const error = validateBlogForm(form);
    if (error) {
      toast(error, "error");
      return;
    }
    setSaving(true);
    try {
      await updateBlog(id, buildBlogFormData(form, faqs, headImageFile));
      toast("Blog post updated!", "success");
      setTimeout(() => navigate("/admin/blogs"), 900);
    } catch (e) {
      toast(e.response?.data?.message || "Error updating blog", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar
        section="blogs"
        onSectionChange={(sid) => navigate("/admin", { state: { section: sid } })}
      />
      <div className="admin-content">
        <div className="admin-section active">
          <div className="admin-header">
            <div className="admin-title">✏️ Edit Blog Post</div>
            <button
              className="btn-secondary btn-sm"
              onClick={() => navigate("/admin/blogs")}
            >
              ← Back to Blogs
            </button>
          </div>

          <div className="admin-card" style={{ maxWidth: 900, padding: 24 }}>
            {notFound ? (
              <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
                Blog post not found.
              </div>
            ) : !form ? (
              <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
                Loading…
              </div>
            ) : (
              <>
                <BlogForm
                  form={form}
                  setForm={setForm}
                  faqs={faqs}
                  setFaqs={setFaqs}
                  headImageFile={headImageFile}
                  setHeadImageFile={setHeadImageFile}
                  existingHeadImage={existingHeadImage}
                  toast={toast}
                />

                <div style={{ display: "flex", gap: 12, marginTop: 28 }}>
                  <button
                    className="btn-primary"
                    onClick={handleSubmit}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save Changes"}
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => navigate("/admin/blogs")}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const EditBlogPage = () => (
  <ToastProvider>
    <EditBlogInner />
  </ToastProvider>
);

export default EditBlogPage;
